import { buttonVariants } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import useAccess from "@/hooks/useAccess";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { setWindowTitle } from "@/lib/window-title";
import { ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

export default function SettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { hasAccess } = useAccess();

  const defaultTab = "language";
  const [activeTab, setActiveTab] = useState<string>(defaultTab);

  const tabList = [
    {
      type: "link",
      label: t("settings.tabs.language"),
      value: "language",
    },
    ...(hasAccess({ requiredLicences: ["basic", "professional"] })
      ? [
          {
            type: "link",
            label: t("settings.tabs.instruction"),
            value: "instruction",
          },
          {
            type: "link",
            label: t("settings.tabs.integrations"),
            value: "/integrations",
            isExternal: true,
          },
        ]
      : []),
  ];

  useEffect(() => {
    const paths = location.pathname.replace("/settings", "").split("/");
    if (paths.length <= 1) {
      navigate(defaultTab, { replace: true });
      return;
    }
    setActiveTab(paths?.[1] ?? defaultTab);
  }, []);

  useEffect(() => {
    setWindowTitle(
      `${t("settings.page.title")} - ${
        tabList.find((tab) => tab.value === activeTab)?.label
      }`
    );
  }, [activeTab, t]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate(value);
  };

  return (
    <>
      <div className="container flex-1 p-8 pt-6 compact:p-6 compact:pt-4 space-y-5">
        <div className="mb-5 space-y-2">
          <h2 className="text-3xl font-bold tracking-tight">
            {t("settings.page.title")}
          </h2>
          <p className="text-muted-foreground">
            {t("settings.page.description")}
          </p>
        </div>
        <Separator className="my-6" />
        <div className="mt-8 grid w-full items-start gap-6 grid-cols-[200px_1fr]">
          <nav className="grid gap-4 text-sm text-muted-foreground">
            {tabList.map((tab, index) => {
              if (tab.type === "link" && tab.value) {
                const isExternal = (tab as any).isExternal;
                return (
                  <Link
                    to={`${tab.value}`}
                    key={tab.value}
                    onClick={(e) => {
                      if (isExternal) {
                        e.preventDefault();
                        window.open(tab.value, "_blank");
                      } else {
                        handleTabChange(tab.value);
                      }
                    }}
                    className={cn(
                      buttonVariants({ variant: "ghost" }),
                      (activeTab === tab.value &&
                        "font-semibold text-primary bg-muted") ||
                        "",
                      "justify-between"
                    )}
                  >
                    {tab.label}
                    {isExternal && <ExternalLink className="w-4 h-4" />}
                  </Link>
                );
              }
              return <Separator key={index} />;
            })}
          </nav>
          <div className="grid gap-6">
            <Outlet />
          </div>
        </div>
      </div>
    </>
  );
}
